import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { apiBaseUrl, apiGet, apiPostBlobJSON, ApiError } from "../api";
// Type-only, and therefore erased: rules.ts imports MusicSource from
// here, so a value import would be a cycle.
import type { Recording } from "./rules";
import type { ID } from "../id";

/**
 * Hudba (Modul 6).
 *
 * A club uploads one MP3 per přihláška and replaces it as often as it
 * likes until the uzávěrka hudby; after that the organizer does it on
 * their behalf. There is deliberately no remove: a competitor on a start
 * list is supposed to have music, so "no music" is a gap to close rather
 * than a state to return to. (DELETE exists on the backend for an
 * admin's purge; nothing here offers it.)
 */

/** Machine keys from internal/service/music. The Czech copy lives in
 * lib/musicState.ts — adding a kind there and here is one change. */
export type MusicWarningKind =
  | "duration_over"
  | "duration_under"
  | "low_bitrate"
  | "low_sample_rate";

export interface MusicWarning {
  kind: MusicWarningKind;
  /** How far outside the limit the recording is; duration warnings only. */
  seconds?: number;
  /** Outside the limit but inside the rule set's grace band, so no
   * deduction follows from it. It still warns — see musicState.ts. */
  within_tolerance?: boolean;
}

export interface EntryMusic {
  original_name: string;
  size_bytes: number;
  duration_ms: number;
  bitrate_kbps: number;
  sample_rate_hz: number;
  uploaded_at: Dayjs;
  /** The combination's effective time limit in seconds, so the player can
   * draw it against the recording. Absent when the rules set none. */
  limit_min_seconds?: number;
  limit_max_seconds?: number;
  warnings: MusicWarning[];
}

/**
 * Who provides the recording for a combination. Only `own` entries can
 * read as missing or offer an upload.
 *
 * `none` is improvizace: the zvukař picks the track at the desk, so
 * there is no file anywhere in the system and none is owed. It is not
 * "federation music that has not arrived yet" — that case is a rule set
 * somebody has to finish, and it blocks the export.
 *
 * Declared here once; `queries/rules.ts` re-exports this type rather
 * than keeping a second copy that can drift.
 */
export type MusicSource = "own" | "federation" | "none";

// Built as an <audio src> / download href: the endpoint takes the session
// cookie, exactly like personPhotoUrl. Never a public URL.
export function entryMusicUrl(entryId: ID): string {
  return `${apiBaseUrl}/entries/${entryId}/music`;
}

/** The bytes of one official ČNSMS recording, cookie-authenticated. */
export function recordingFileUrl(recordingId: ID): string {
  return `${apiBaseUrl}/rules/recordings/${recordingId}/file`;
}

export function competitionMusicExportUrl(competitionId: ID): string {
  return `${apiBaseUrl}/competitions/${competitionId}/music/export`;
}

/** One performance the archive is waiting on. */
export interface MissingMusicRow {
  start_number: number;
  team_name: string;
  title: string;
  category: string;
  /** The rarer case: the discipline is danced to the official ČNSMS track
   * and the rule set has none on file — an admin's problem, not a club's. */
  federation?: boolean;
}

/**
 * Whether the ZIP can be built, straight from the walk that builds it.
 *
 * The page renders its banner and its button from this rather than
 * deciding for itself what „chybí hudba" means. Its own rule counted
 * performances the export skips — a scratched row owes nothing — and
 * left an alert up and a button dead over an archive the server would
 * have produced happily.
 */
export interface MusicExportStatus {
  published: boolean;
  ready: boolean;
  files: number;
  missing: MissingMusicRow[];
}

export function useMusicExportStatus(competitionId: ID) {
  return useQuery<MusicExportStatus>({
    queryKey: ["competitions", competitionId, "music", "export-status"],
    queryFn: () =>
      apiGet<MusicExportStatus>(`/competitions/${competitionId}/music/export/status`),
    enabled: !!competitionId,
    // Like the start list it is derived from: several people are moving
    // this document at once, so thirty seconds of cache is thirty
    // seconds of a wrong answer about whether the archive is ready.
    staleTime: 0,
  });
}

/** `name` travels as a query parameter because the body is the raw file:
 * there is no multipart envelope to carry a filename in. */
function withName(url: string, fileName: string): string {
  return `${url}?name=${encodeURIComponent(fileName)}`;
}

/**
 * Revives the timestamp the raw upload returns.
 *
 * apiPostBlobJSON does its own fetch, so the rest client's date reviver
 * never sees this body — `uploaded_at` arrives as a string while the same
 * field is a Dayjs everywhere it comes from apiGet. TypeScript cannot see
 * the difference, so the first formatDateTime() written against it would
 * be the one to find out.
 */
function reviveMusic(raw: EntryMusic): EntryMusic {
  return { ...raw, uploaded_at: dayjs(raw.uploaded_at as unknown as string) };
}

export function useUploadEntryMusic(entryId: ID) {
  const qc = useQueryClient();
  return useMutation<EntryMusic, ApiError, File>({
    mutationFn: (file) =>
      apiPostBlobJSON<EntryMusic>(withName(`/entries/${entryId}/music`, file.name), file).then(
        reviveMusic,
      ),
    // MusicPanel renders the error inline; a toast would say the
    // same thing twice.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      // A recording changes the entry lists on both sides of the module
      // (the club's and the organizer's) and clears a dashboard alert.
      qc.invalidateQueries({ queryKey: ["teams"] });
      qc.invalidateQueries({ queryKey: ["competitions"] });
      qc.invalidateQueries({ queryKey: ["startlists"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "alerts"] });
    },
  });
}

/**
 * Adds an official ČNSMS recording to a rule set: the MP3 as a raw body,
 * the display name and the file name in the query string.
 *
 * MusicPanel drives both this and the replacement below, so both keep
 * the `File`-in, recording-out shape its `upload` prop expects.
 */
export function useCreateRecording(ruleSetId: ID, name: string) {
  const qc = useQueryClient();
  return useMutation<Recording, ApiError, File>({
    mutationFn: (file) =>
      apiPostBlobJSON<Recording>(
        `/rules/rulesets/${ruleSetId}/recordings?name=${encodeURIComponent(name)}` +
          `&filename=${encodeURIComponent(file.name)}`,
        file,
      ).then(reviveRecording),
    meta: { suppressErrorToast: true },
    onSuccess: () => invalidateRules(qc),
  });
}

/** Swaps the bytes of an existing recording, keeping its name and every
 * reference to it. */
export function useReplaceRecordingFile(recordingId: ID) {
  const qc = useQueryClient();
  return useMutation<Recording, ApiError, File>({
    mutationFn: (file) =>
      apiPostBlobJSON<Recording>(
        `/rules/recordings/${recordingId}/file?filename=${encodeURIComponent(file.name)}`,
        file,
      ).then(reviveRecording),
    meta: { suppressErrorToast: true },
    onSuccess: () => invalidateRules(qc),
  });
}

function invalidateRules(qc: ReturnType<typeof useQueryClient>): void {
  qc.invalidateQueries({ queryKey: ["admin", "rulesets"] });
  qc.invalidateQueries({ queryKey: ["rules", "document"] });
}

/** Same reason as reviveMusic: apiPostBlobJSON bypasses the rest
 * client's date reviver. */
function reviveRecording(raw: Recording): Recording {
  return {
    ...raw,
    created_at: dayjs(raw.created_at as unknown as string),
    updated_at: dayjs(raw.updated_at as unknown as string),
  };
}
